import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Phone, X } from 'lucide-react';
import LOGO_URL from '../../assets/logo.jpeg';
import KNOCKOUT_LOGO_URL from '../../assets/futuremakers-logo-knockout.png';
import {
  PHONE_PRIMARY_DISPLAY,
  PHONE_PRIMARY_E164,
  PHONE_SECONDARY_E164,
  TEL_PRIMARY_HREF,
  WHATSAPP_URL,
  isResolved,
} from '../../config/contact';

/*
 * Shared shell for the public marketing pages ("/" and "/results"): scroll
 * container, header, footer and the fixed mobile call bar, extracted verbatim
 * from LandingPage.js so the two pages cannot drift apart visually.
 *
 * index.css sets `html, body { overflow: hidden }` app-wide, so the shell owns
 * its own scroll container (h-screen overflow-y-auto) the same way
 * DashboardLayout and StudentLayout do. Anchor navigation therefore scrolls
 * that container, not the document — see scrollToSection below. On any route
 * other than "/", a section link navigates to "/#id" instead, and the
 * hash-on-mount effect finishes the scroll once the landing page renders.
 *
 * Children may be a plain node, or a function receiving scrollToSection for
 * in-page anchor CTAs (the landing page's hero/category links use this).
 */

/* Links with `id` scroll to an on-page landing section; links with `to` are
   router routes. "Results" is the poster archive at /results; "Stories" is
   the video testimonials at /stories. Neither is an on-page anchor. */
const NAV_LINKS = [
  { id: 'programs', label: 'Programs' },
  { id: 'exams', label: 'Exam prep' },
  { to: '/results', label: 'Results' },
  { to: '/stories', label: 'Stories' },
  { id: 'centres', label: 'Centres' },
];

/* ---- Navbar collapse breakpoint (single source) ----
   The desktop nav — including the amber Call button — exists at this breakpoint
   and up; the mobile header controls, mobile menu, and the fixed mobile call
   bar exist exactly below it. Tailwind's JIT scans source text and cannot see
   interpolated class fragments, so the breakpoint is spelled as full class
   literals: change ALL THREE together, never one. Measured (2026-08-02): at
   1024px the slimmed bar (logo + 4 links + Login + Call button) fits with no
   overflow, so the collapse moved from xl down to lg. */
const NAV_DESKTOP_CLASS = 'hidden lg:flex';
const NAV_MOBILE_CLASS = 'lg:hidden';
/* Bottom clearance on the root scroll container so the footer is never occluded
   by the fixed mobile call bar (52px bar + margin + iOS safe-area inset). */
const NAV_MOBILE_CLEARANCE_CLASS = 'pb-[calc(64px+env(safe-area-inset-bottom))] lg:pb-0';

/*
 * Both footer centre blocks dial this. It resolves to the primary number today
 * and will keep doing so until PHONE_SECONDARY_E164 is filled in — which is
 * correct: both live Google Business Profile listings carry the primary line,
 * and NAP consistency with Google governs.
 *
 * The point of computing it here rather than reusing TEL_PRIMARY_HREF is that
 * PHONE_SECONDARY_E164 is now referenced from the render path, so the pending
 * per-centre split is greppable from the code that actually renders the buttons
 * instead of living only in a comment.
 *
 * NOT `PHONE_SECONDARY_E164 ?? PHONE_PRIMARY_E164`: the unresolved value is the
 * placeholder STRING '[SECOND_CENTRE_NUMBER_E164]', not null/undefined, so `??`
 * would never fall back and would emit a dead tel:[SECOND_CENTRE_NUMBER_E164]
 * href. isResolved() is the check this config already uses for exactly this
 * (see WHATSAPP_URL).
 *
 * When the split lands, this single expression switches BOTH centres at once —
 * see the note at PHONE_SECONDARY_E164 in config/contact.js.
 */
const centreTelHref = `tel:${
  isResolved(PHONE_SECONDARY_E164) ? PHONE_SECONDARY_E164 : PHONE_PRIMARY_E164
}`;

/* WhatsApp elements render as real links only once WHATSAPP_E164 resolves to a
   real number (see src/config/contact.js) — WHATSAPP_URL is null otherwise, and
   the element renders inert with a visible NEEDS_NUMBER marker rather than
   falling back to the phone number or shipping silently. */
export const WhatsAppLink = ({ className = '', children }) =>
  WHATSAPP_URL ? (
    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  ) : (
    <span className={`${className} cursor-not-allowed opacity-60`} title="[NEEDS_NUMBER]">
      {children}
      <span className="font-mono text-[9px] align-super ml-1">[NEEDS_NUMBER]</span>
    </span>
  );

/* WhatsApp brand glyph — lucide has no WhatsApp icon and adding an icon pack for
   one glyph isn't warranted, so the standard path is inlined. */
export const WhatsAppIcon = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/* The old brand mark on its white pill — footer only. The header uses the
   knockout PNG; Sidebar.js and LoginPage.js keep this same asset. */
const Logo = ({ size = 'md' }) => {
  const height = size === 'sm' ? 'h-11 lg:h-12' : 'h-12';
  return (
    <img
      src={LOGO_URL}
      alt="Future Makers"
      className={`${height} w-auto shrink-0 rounded-lg bg-white p-1`}
    />
  );
};

const LandingShell = ({ children }) => {
  const scrollRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  /* The page scrolls inside scrollRef, not the document, so a bare `href="#id"`
     fragment jump is unreliable here. On "/", scroll the section into view and
     keep the URL fragment in sync so anchors remain linkable/shareable; on any
     other public page, navigate to the landing page with the fragment and let
     the hash effect below finish the job. */
  const scrollToSection = useCallback(
    (event, id) => {
      event.preventDefault();
      setMenuOpen(false);
      if (location.pathname !== '/') {
        navigate(`/#${id}`);
        return;
      }
      const target = scrollRef.current?.querySelector(`#${id}`);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${id}`);
    },
    [location.pathname, navigate]
  );

  /* Honor a router-supplied fragment on mount/navigation (e.g. /results nav →
     "/#programs"). In-page clicks use replaceState, which react-router never
     sees, so this only fires on real navigations. */
  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!id) return;
    requestAnimationFrame(() => {
      const target = scrollRef.current?.querySelector(`#${CSS.escape(id)}`);
      target?.scrollIntoView({ block: 'start' });
    });
  }, [location.pathname, location.hash]);

  return (
    <div
      ref={scrollRef}
      className={`h-screen overflow-y-auto overflow-x-hidden bg-white font-display antialiased ${NAV_MOBILE_CLEARANCE_CLASS}`}
    >
      {/* ---------- Header ---------- */}
      <header className="sticky top-0 z-30 bg-brand-deep">
        {/* h-20 (80px) bar so the h-14 knockout wordmark breathes. max-w stays on
            the site's 1180px content grid. */}
        <div className="mx-auto max-w-[1180px] h-20 flex items-center justify-between gap-3 lg:gap-5 px-4 lg:px-8 xl:px-11">
          {/* Knockout wordmark straight onto the deep-teal bar — no white pill.
              The footer and the authenticated shell keep the old logo.jpeg. */}
          <Link to="/" aria-label="Future Makers — home" className="shrink-0">
            <img src={KNOCKOUT_LOGO_URL} alt="Future Makers" className="h-14 w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className={`${NAV_DESKTOP_CLASS} items-center gap-4 text-[16px] font-medium text-white/[0.78]`}>
            {NAV_LINKS.map((link) =>
              link.to ? (
                <Link
                  key={link.label}
                  to={link.to}
                  className="py-2 whitespace-nowrap hover:text-white transition-colors"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={`#${link.id}`}
                  onClick={(e) => scrollToSection(e, link.id)}
                  className="py-2 whitespace-nowrap hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              )
            )}
          </nav>

          {/* Desktop actions: one primary CTA (Call), one ghost Login link */}
          <div className={`${NAV_DESKTOP_CLASS} items-center gap-4`}>
            <Link
              to="/login"
              className="text-[15px] font-semibold text-white/[0.78] whitespace-nowrap hover:text-white transition-colors"
            >
              Login →
            </Link>
            <a
              href={TEL_PRIMARY_HREF}
              className="flex items-center gap-2 px-5 py-3 rounded-lg bg-brand-amber text-[15.5px] font-bold text-brand-deep whitespace-nowrap"
            >
              <Phone className="w-[18px] h-[18px]" />
              Call {PHONE_PRIMARY_DISPLAY}
            </a>
          </div>

          {/* Mobile actions */}
          <div className={`flex ${NAV_MOBILE_CLASS} items-center gap-2`}>
            <a
              href={TEL_PRIMARY_HREF}
              aria-label="Call Future Makers"
              className="w-12 h-12 rounded-xl bg-white/[0.12] flex items-center justify-center text-white"
            >
              <Phone className="w-[22px] h-[22px]" />
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              className="w-12 h-12 rounded-xl bg-white/[0.12] flex items-center justify-center text-white"
            >
              {menuOpen ? <X className="w-[22px] h-[22px]" /> : <Menu className="w-[22px] h-[22px]" />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {menuOpen && (
          <div className={`${NAV_MOBILE_CLASS} border-t border-white/[0.14] bg-brand-deep px-4 py-4 flex flex-col gap-1`}>
            {NAV_LINKS.map((link) =>
              link.to ? (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="py-3.5 text-[21px] font-medium text-white/80"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={`#${link.id}`}
                  onClick={(e) => scrollToSection(e, link.id)}
                  className="py-3.5 text-[21px] font-medium text-white/80"
                >
                  {link.label}
                </a>
              )
            )}
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              className="mt-2 px-4 py-4 rounded-xl border border-white/35 text-[17px] font-semibold text-white text-center"
            >
              Login →
            </Link>
          </div>
        )}
      </header>

      {typeof children === 'function' ? children(scrollToSection) : children}

      {/* ---------- Centres / footer (#centres) ---------- */}
      <footer id="centres" className="scroll-mt-16 lg:scroll-mt-20 bg-brand-deeper">
        <div className="mx-auto max-w-[1180px] px-5 pt-8 pb-6 lg:px-11 lg:pt-[60px] lg:pb-[30px]">
          <div className="lg:grid lg:grid-cols-[1.2fr_1fr_1fr_1fr] lg:gap-10 lg:pb-9 lg:border-b lg:border-white/[0.14]">
            <div>
              <div className="mb-2.5 lg:mb-3">
                <Logo size="sm" />
              </div>
              <p className="text-[13px] lg:text-sm leading-relaxed text-white/60 lg:max-w-[30ch] mb-[26px] lg:mb-0">
                Rigorous academics, values-driven learning, global-ready skills.
              </p>
            </div>

            <div className="flex flex-col gap-[22px] lg:contents">
              <div>
                <span className="block text-[10.5px] lg:text-[11.5px] font-bold text-white/45 tracking-[0.12em] mb-2 lg:mb-3">
                  SECTOR 49 CENTRE
                </span>
                <p className="text-[13.5px] lg:text-sm leading-relaxed text-white/85 mb-3">
                  2nd Floor, 201B, Unitech Arcadia Market,
                  <br />
                  South City II, Sector 49,
                  <br />
                  Gurugram, Haryana 122018
                </p>
                <a
                  href={centreTelHref}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-brand-amber text-[12.5px] font-bold text-brand-deep"
                >
                  <Phone className="w-[14px] h-[14px]" />
                  Call this centre
                </a>
              </div>

              <div>
                <span className="block text-[10.5px] lg:text-[11.5px] font-bold text-white/45 tracking-[0.12em] mb-2 lg:mb-3">
                  SECTOR 52 CENTRE
                </span>
                <p className="text-[13.5px] lg:text-sm leading-relaxed text-white/85 mb-3">
                  Ground floor, C-10, Block C,
                  <br />
                  Ardee City, Sector 52,
                  <br />
                  Gurugram, Haryana 122003
                </p>
                <a
                  href={centreTelHref}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-brand-amber text-[12.5px] font-bold text-brand-deep"
                >
                  <Phone className="w-[14px] h-[14px]" />
                  Call this centre
                </a>
              </div>

              {/* #enroll target: the design's Enroll CTAs point here, at contact details. */}
              <div id="enroll" className="scroll-mt-16 lg:scroll-mt-20">
                <span className="block text-[10.5px] lg:text-[11.5px] font-bold text-white/45 tracking-[0.12em] mb-2 lg:mb-3">
                  CONTACT
                </span>
                <p className="text-[13.5px] lg:text-sm leading-relaxed text-white/85 lg:mb-3.5">
                  <a href={TEL_PRIMARY_HREF} className="hover:underline">
                    {PHONE_PRIMARY_DISPLAY}
                  </a>
                  <br />
                  info@futuremakers.co.in
                  <br />
                  <WhatsAppLink className="hover:underline">WhatsApp us</WhatsAppLink>
                </p>
                <div className="hidden lg:flex gap-2">
                  {['Instagram', 'YouTube', 'Facebook'].map((name) => (
                    <span
                      key={name}
                      className="px-3 py-1.5 rounded-[7px] bg-white/10 text-[11.5px] font-medium text-white"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 lg:hidden">
                {['Instagram', 'YouTube', 'Facebook'].map((name) => (
                  <span
                    key={name}
                    className="px-3.5 py-2.5 rounded-lg bg-white/10 text-xs font-medium text-white"
                  >
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-[26px] pt-4 border-t border-white/[0.14] text-[11.5px] text-white/40 lg:mt-0 lg:pt-[18px] lg:border-t-0 lg:flex lg:justify-between lg:text-xs">
            <span className="lg:hidden">© 2026 Future Makers · Privacy · Terms</span>
            <span className="hidden lg:inline">© 2026 Future Makers Coaching Institute</span>
            <span className="hidden lg:inline">Privacy · Terms</span>
          </div>
        </div>
      </footer>

      {/* ---------- Mobile fixed call bar ---------- */}
      {/* Fixed, not sticky: visible from first paint, not only once the user
          reaches the bottom of the scroll container. Exists exactly when the
          navbar's Call button does not (NAV_MOBILE_CLASS); the root scroll
          container carries NAV_MOBILE_CLEARANCE_CLASS so the footer clears it.
          WhatsAppLink keeps the half inert-with-marker if the WhatsApp number
          ever reverts to a placeholder. */}
      <div
        className={`${NAV_MOBILE_CLASS} fixed bottom-0 inset-x-0 z-30 flex bg-brand-deeper`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <WhatsAppLink className="flex-1 min-h-[52px] flex items-center justify-center gap-2 bg-[#25D366] text-[15px] font-bold text-white">
          <WhatsAppIcon />
          WhatsApp
        </WhatsAppLink>
        <a
          href={TEL_PRIMARY_HREF}
          className="flex-1 min-h-[52px] flex items-center justify-center gap-2 bg-brand-amber text-[15px] font-bold text-brand-deep"
        >
          <Phone className="w-[18px] h-[18px]" />
          Call
        </a>
      </div>
    </div>
  );
};

export default LandingShell;
